/**
 * URL filters and bulk-selection rules for the Marketing Audience screen.
 *
 * Kept out of the component so the rules that decide WHICH contacts a
 * destructive action may touch are unit-tested without a DOM.
 *
 * The morning digest deep-links here with `?state=VERIFIED`,
 * `?delete_eligible=true`, `?cleanup=true`, `?conflict=true` or
 * `?held=true`. Only these known keys are read; anything else in the URL
 * is ignored (the digest's `src`/`digest`/`snap` context is read by
 * `digestOrigin.js`, never as a filter).
 */

export const AUDIENCE_STATES = [
  "ACTIVE", "VERIFIED", "SALES_OWNED", "SUPPRESSED", "INVALID",
  "HISTORICALLY_ENGAGED", "NEVER_ENGAGED", "STALE", "TERMINAL", "REVIEW",
];

const TEXT_KEYS = ["state", "reason", "q", "source", "domain", "sales_stage", "email", "bad_reason", "sort", "dir"];
const BOOL_KEYS = ["delete_eligible", "cleanup", "conflict", "releasable", "held", "bad_address", "test_data"];

// Bad-address categories (marketing_audience.BAD_ADDRESS_CATEGORIES).
export const BAD_ADDRESS_CATEGORIES = {
  hard_bounce: "Hard bounce",
  invalid_syntax: "Invalid syntax",
  dead_domain: "Dead / no-mail domain",
  crm_invalid: "CRM-invalid and never delivered",
  other_permanent: "Other permanent delivery failure",
};

/** URL query string -> filter object. Unknown keys and bad values dropped. */
export function filtersFromSearch(search) {
  const p = new URLSearchParams(search || "");
  const f = {};
  TEXT_KEYS.forEach((k) => {
    const v = (p.get(k) || "").trim();
    if (v) f[k] = v;
  });
  if (f.state) {
    f.state = f.state.toUpperCase();
    if (!AUDIENCE_STATES.includes(f.state)) delete f.state;
  }
  if (f.dir && f.dir !== "asc" && f.dir !== "desc") delete f.dir;
  BOOL_KEYS.forEach((k) => {
    const v = (p.get(k) || "").trim().toLowerCase();
    if (v === "true" || v === "1") f[k] = true;
    else if (v === "false" || v === "0") f[k] = false;
  });
  return f;
}

/** Filter object -> query string (no leading "?"), stable key order. */
export function searchFromFilters(f) {
  const p = new URLSearchParams();
  [...TEXT_KEYS, ...BOOL_KEYS].forEach((k) => {
    const v = f?.[k];
    if (v === undefined || v === null || v === "") return;
    p.set(k, typeof v === "boolean" ? String(v) : v);
  });
  return p.toString();
}

// Plain-English names for the boolean filters (the active-filter bar).
const BOOL_LABELS = {
  delete_eligible: ["Eligible for garbage cleanup", "Not eligible for garbage cleanup"],
  cleanup: ["Enrolment rows that can never send (stranded)", "No stranded enrolment rows"],
  conflict: ["Automatic email despite Sales ownership or quarantine", "No automatic-email conflict"],
  releasable: ["Releasable in stages", "Not releasable"],
  held: ["Has paused (held) enrolments", "No paused enrolments"],
  bad_address: ["Bad / undeliverable address", "Address not proven bad"],
  test_data: ["Eyvo staff / test data", "Not test data"],
};
const TEXT_LABELS = {
  q: "Search", source: "Lead source", domain: "Domain", sales_stage: "Sales stage",
};

/**
 * The active filters as readable phrases, so an operator who arrives from
 * a link can see why only part of the audience is listed. `stateLabels`
 * maps state -> label; `reasonLabels` reason_code -> label (both optional).
 */
export function describeFilters(f, stateLabels = {}, reasonLabels = {}) {
  const out = [];
  if (f?.state) out.push(stateLabels[f.state] || f.state);
  if (f?.reason) out.push(`Reason: ${reasonLabels[f.reason] || f.reason}`);
  if (f?.bad_reason) out.push(BAD_ADDRESS_CATEGORIES[f.bad_reason] || f.bad_reason);
  BOOL_KEYS.forEach((k) => {
    if (f?.[k] === true) out.push(BOOL_LABELS[k][0]);
    else if (f?.[k] === false) out.push(BOOL_LABELS[k][1]);
  });
  Object.entries(TEXT_LABELS).forEach(([k, label]) => {
    if (f?.[k]) out.push(`${label}: ${f[k]}`);
  });
  if (f?.email) {
    const n = f.email.split(",").filter((e) => e.trim()).length;
    out.push(n === 1 ? `Address: ${f.email}` : `${n} specific addresses (from Email Activity)`);
  }
  return out;
}

/**
 * Audience link for exactly these addresses — Email Activity's bridge from
 * a failed delivery to the screen where the contact can be deleted or
 * retired. Duplicates and blanks dropped; capped so the URL stays usable.
 */
export function audienceLinkForEmails(emails, max = 100) {
  const unique = Array.from(new Set((emails || []).map((e) => (e || "").trim().toLowerCase()).filter(Boolean)));
  if (!unique.length) return "";
  return `/marketing/audience?${new URLSearchParams({ email: unique.slice(0, max).join(",") }).toString()}`;
}

/** Request params for GET /marketing/audience/contacts. */
export function apiParams(f, offset = 0, limit = 100) {
  const out = { offset, limit };
  [...TEXT_KEYS, ...BOOL_KEYS].forEach((k) => {
    const v = f?.[k];
    if (v !== undefined && v !== null && v !== "") out[k] = v;
  });
  return out;
}

/**
 * The selected contacts a permanent delete may be ASKED about: only the
 * ones the server last reported as deletion-safe. The server re-checks
 * every one of them again before deleting; this is the first filter, not
 * the control.
 */
export function deletableSelection(selectedIds, eligibleIds) {
  const eligible = new Set(eligibleIds || []);
  return (selectedIds || []).filter((id) => eligible.has(id));
}

/** Split ids into request-sized batches. */
export function chunk(ids, size) {
  const n = Math.max(1, Number(size) || 1);
  const out = [];
  for (let i = 0; i < (ids || []).length; i += n) out.push(ids.slice(i, i + n));
  return out;
}

/** Combine per-batch action responses into one result. */
export function mergeResults(responses) {
  const results = [];
  const outcomes = {};
  (responses || []).forEach((r) => {
    (r?.results || []).forEach((x) => results.push(x));
    Object.entries(r?.outcomes || {}).forEach(([k, v]) => {
      outcomes[k] = (outcomes[k] || 0) + v;
    });
  });
  return { results, outcomes };
}

const SUCCESS_OUTCOMES = new Set(["deleted", "retired", "removed", "set", "cleared",
  "would_delete", "would_retire", "would_remove"]);

/**
 * The selected contacts an action may be ASKED about: only those the
 * server last reported as eligible for it. The server re-checks each one
 * on fresh evidence before acting; this is the first filter, not the control.
 */
export function eligibleSelection(selectedIds, eligibleIds) {
  return deletableSelection(selectedIds, eligibleIds);
}

/** True only when every contact reached the outcome the operator asked for. */
export function isCleanSuccess(outcomes) {
  const keys = Object.keys(outcomes || {}).filter((k) => outcomes[k] > 0);
  return keys.length > 0 && keys.every((k) => SUCCESS_OUTCOMES.has(k));
}

/** "3 deleted · 1 skipped" from an outcomes map. */
export function outcomeSummary(outcomes) {
  const words = {
    deleted: "deleted", would_delete: "would be deleted", retired: "retired",
    would_retire: "would be retired", removed: "removed (lead archived)",
    would_remove: "would be removed", skipped: "skipped", error: "failed",
    changed_concurrently: "changed while running (skipped)",
    set: "updated", cleared: "cleared", refused: "refused", not_found: "not found",
  };
  return Object.entries(outcomes || {})
    .map(([k, v]) => `${v} ${words[k] || k}`)
    .join(" · ");
}
